import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useViewMode } from '../contexts/ViewModeContext';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, X,
  Loader2, Film, Image, Camera, Video, MoreHorizontal, Filter,
  CheckSquare, BarChart3, Users, Building, User, Music, Download, Table,
  FileText, AlertCircle, Check, ExternalLink, Link2, LayoutGrid, List
} from 'lucide-react';
import TaskModal from './TaskModal';
import EventCalendar from './EventCalendar';
import SOPModal from './calendar/SOPModal';
import DragConfirmDialog from './calendar/DragConfirmDialog';
import { getLighterColor } from '../lib/clientColors';
import { formatDateToLocal, parseLocalDate, isSameDay, formatDisplayDate } from '../lib/dateUtils';
import { calculateDueDate as calcDueDateUtil, calculateDueDateByGap, SOP_OPTIONS } from '../lib/sopRules';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Helper function to extract URLs from text
const extractUrlsFromText = (text) => {
  if (!text) return [];
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
  const matches = text.match(urlRegex) || [];
  return [...new Set(matches.map(url => 
    url.startsWith('www.') ? `https://${url}` : url
  ))];
};

// SOP Modal — extracted to ./calendar/SOPModal.js for maintainability.
// (Phase 4 decomposition — see PRD.md)

export default function CalendarsPage({ api, userType = 'admin' }) {
  const { isMobileView } = useViewMode();
  const [activeTab, setActiveTab] = useState('social-media');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [dayDetailDate, setDayDetailDate] = useState(null);
  const [calendarViewMode, setCalendarViewMode] = useState('grid');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskTypes, setTaskTypes] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  
  // SOP Modal state
  const [showSOPModal, setShowSOPModal] = useState(false);
  const [sopLoading, setSOPLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [holidays, setHolidays] = useState([]);
  
  // Drag-and-drop state
  const [draggedTask, setDraggedTask] = useState(null);
  const [showDragConfirm, setShowDragConfirm] = useState(false);
  const [pendingDragUpdate, setPendingDragUpdate] = useState(null);
  
  // Filter states
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubtypes, setSelectedSubtypes] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  // Social Media Calendar defaults to "My Clients" for employees
  const [myClientsOnly, setMyClientsOnly] = useState(userType === 'employee');
  // Production Calendar defaults to showing all clients
  const [showAllClients, setShowAllClients] = useState(true);
  
  // Raw server response (only refetched when server-side params change)
  const [rawTasks, setRawTasks] = useState([]);
  
  // Boolean helpers used in fetchTasks deps (avoid in-place expressions in dep arrays)
  const hasSelectedClients = selectedClients.length > 0;
  
  // Check if any filter is active (for admin/employee)
  // For Production Calendar, "All Clients" (showAllClients) counts as an active filter
  const hasActiveFilters = selectedClients.length > 0 || selectedCategories.length > 0 || 
                           selectedSubtypes.length > 0 || selectedAssignees.length > 0 || myClientsOnly ||
                           (activeTab === 'production' && showAllClients);
  
  // Dropdown states
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSubtypeDropdown, setShowSubtypeDropdown] = useState(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);

  // Fetch current user ID on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const endpoint = userType === 'admin' ? '/api/admin/auth/me' : 
                        userType === 'employee' ? '/api/employee/me' : null;
        if (endpoint) {
          const res = await api.get(endpoint);
          setCurrentUserId(res.data?._id || res.data?.id);
        }
      } catch (error) {
        console.error('Failed to fetch current user:', error);
      }
    };
    fetchCurrentUser();
  }, [api, userType]);

  const calendarConfig = {
    'social-media': {
      title: 'Social Media Calendar',
      taskType: 'Social Media Calendar',
      color: 'pink',
      icon: Image,
      subtypes: ['Reel', 'Post', 'Story'],
      subtypeIcons: {
        'Reel': Film,
        'Post': Image,
        'Story': Camera
      }
    },
    'production': {
      title: 'Production Calendar',
      taskType: 'Production',
      color: 'blue',
      icon: Video,
      subtypes: ['Calendar Shoot', 'Menu Shoot', 'FPV', 'Drone', 'Others'],
      subtypeIcons: {
        'Calendar Shoot': CalendarIcon,
        'Menu Shoot': Camera,
        'FPV': Video,
        'Drone': Video,
        'Others': MoreHorizontal
      }
    }
  };

  const currentConfig = calendarConfig[activeTab] || {
    title: 'Calendar',
    taskType: '',
    color: 'gray',
    icon: CalendarIcon,
    subtypes: [],
    subtypeIcons: {}
  };

  // For clients portal, auto-apply filter
  const isClientPortal = userType === 'client';
  const isEmployeePortal = userType === 'employee';
  const isAdminPortal = userType === 'admin';

  const getApiEndpoint = useCallback(() => {
    switch(userType) {
      case 'admin': return '/api/admin/tasks';
      case 'employee': return '/api/employee/tasks';
      case 'client': return '/api/client/tasks';
      default: return '/api/admin/tasks';
    }
  }, [userType]);

  const fetchClients = useCallback(async () => {
    try {
      const endpoint = userType === 'admin' ? '/api/admin/clients' : '/api/employee/clients';
      const res = await api.get(endpoint);
      setClients(res.data || []);
      
      // Extract unique categories
      const cats = [...new Set((res.data || []).map(c => c.category).filter(Boolean))];
      setCategories(cats);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  }, [api, userType]);

  const fetchEmployees = useCallback(async () => {
    try {
      const endpoint = userType === 'admin' ? '/api/admin/employees' : '/api/employee/team';
      const res = await api.get(endpoint);
      setEmployees(res.data || []);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  }, [api, userType]);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await api.get('/api/departments');
      setDepartments(res.data || []);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  }, [api]);

  const fetchHolidays = useCallback(async () => {
    try {
      const res = await api.get('/api/holidays');
      // Combine mandatory and optional holidays into a flat array
      const mandatory = res.data?.mandatory || [];
      const optional = res.data?.optional || [];
      setHolidays([...mandatory, ...optional]);
    } catch (error) {
      console.error('Failed to load holidays:', error);
      setHolidays([]);
    }
  }, [api]);

  const fetchTasks = useCallback(async ({ silent = false } = {}) => {
    // For admin/employee, only fetch when at least one filter is active (except for client portal)
    if ((isAdminPortal || isEmployeePortal) && !hasActiveFilters) {
      setRawTasks([]);
      return;
    }

    if (!silent) setLoading(true);
    try {
      const endpoint = getApiEndpoint();
      const params = { task_type: currentConfig.taskType };
      
      // For employees with "My Clients" filter - this is handled by backend
      if (isEmployeePortal && myClientsOnly) {
        params.my_clients = 'true';
      }
      
      // For employees selecting individual clients, we need to fetch all tasks first
      // then filter client-side (since view_mode="my" would only return assigned tasks)
      if (isEmployeePortal && selectedClients.length > 0 && !myClientsOnly) {
        params.view_mode = 'all';
      }

      const res = await api.get(endpoint, { params });
      setRawTasks(res.data || []);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
    // Only server-relevant params should be in deps. Client-side filters
    // (selectedClients, selectedCategories, selectedSubtypes, selectedAssignees)
    // are applied via useMemo below without refetching.
  }, [api, currentConfig.taskType, getApiEndpoint, myClientsOnly, hasActiveFilters, isAdminPortal, isEmployeePortal, hasSelectedClients]);

  const fetchTaskTypes = useCallback(async () => {
    try {
      const res = await api.get('/api/task-types');
      setTaskTypes(res.data || []);
    } catch (error) {
      console.error('Failed to load task types:', error);
    }
  }, [api]);

  // ---- Client-side filtered tasks (no refetch on filter change) ----
  const tasks = useMemo(() => {
    let filtered = rawTasks;
    if (selectedClients.length > 0) {
      const s = new Set(selectedClients);
      filtered = filtered.filter(t => s.has(t.client_id));
    }
    if (selectedCategories.length > 0) {
      const catSet = new Set(selectedCategories);
      const clientIds = new Set(
        clients.filter(c => catSet.has(c.category)).map(c => c._id)
      );
      filtered = filtered.filter(t => clientIds.has(t.client_id));
    }
    if (selectedSubtypes.length > 0) {
      const s = new Set(selectedSubtypes);
      filtered = filtered.filter(t => s.has(t.task_subtype));
    }
    if (selectedAssignees.length > 0) {
      const s = new Set(selectedAssignees);
      filtered = filtered.filter(t =>
        s.has(t.primary_assignee_id) ||
        (t.collaborators && t.collaborators.some(c => s.has(c)))
      );
    }
    return filtered;
  }, [rawTasks, selectedClients, selectedCategories, selectedSubtypes, selectedAssignees, clients]);

  // ---- Bucket tasks by date-string for O(1) calendar lookups ----
  const tasksByDate = useMemo(() => {
    const map = new Map();
    const useUseDate = activeTab === 'social-media';
    for (const t of tasks) {
      const key = useUseDate ? (t.use_date || t.due_date) : t.due_date;
      if (!key) continue;
      const arr = map.get(key);
      if (arr) arr.push(t); else map.set(key, [t]);
    }
    return map;
  }, [tasks, activeTab]);

  // ---- Derived metrics (scoped to the currently displayed month) ----
  // Mirrors the same window the calendar grid renders: use_date for
  // social-media tasks, due_date as fallback. Honors all active client /
  // category / subtype / assignee filters via `tasks`.
  const monthScopedTasks = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = `${year}-${String(month + 1).padStart(2, '0')}-${new Date(year, month + 1, 0).getDate()}`;
    const useUseDate = activeTab === 'social-media';
    return tasks.filter(t => {
      const key = useUseDate ? (t.use_date || t.due_date) : t.due_date;
      return key && key >= firstDay && key <= lastDay;
    });
  }, [tasks, currentDate, activeTab]);

  const metrics = useMemo(() => ({
    total: monthScopedTasks.length,
    reels: monthScopedTasks.filter(t => t.task_subtype === 'Reel').length,
    posts: monthScopedTasks.filter(t => t.task_subtype === 'Post').length,
    stories: monthScopedTasks.filter(t => t.task_subtype === 'Story').length,
  }), [monthScopedTasks]);

  const productionMetrics = useMemo(() => ({
    total: monthScopedTasks.length,
    calendarShoot: monthScopedTasks.filter(t => t.task_subtype === 'Calendar Shoot').length,
    menuShoot: monthScopedTasks.filter(t => t.task_subtype === 'Menu Shoot').length,
    fpv: monthScopedTasks.filter(t => t.task_subtype === 'FPV').length,
    drone: monthScopedTasks.filter(t => t.task_subtype === 'Drone').length,
    others: monthScopedTasks.filter(t => t.task_subtype === 'Others').length,
  }), [monthScopedTasks]);

  useEffect(() => {
    // Parallelize mount fetches for fast first paint
    fetchTaskTypes();
    if (!isClientPortal) {
      Promise.all([
        fetchClients(),
        fetchEmployees(),
        fetchDepartments(),
        fetchHolidays()
      ]);
    }
  }, [fetchTaskTypes, fetchClients, fetchEmployees, fetchDepartments, fetchHolidays, isClientPortal]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    // Reset filters when switching tabs
    setSelectedClients([]);
    setSelectedCategories([]);
    setSelectedSubtypes([]);
    setSelectedAssignees([]);
    
    // Social Media Calendar: Default to "My Clients" for employees
    // Production Calendar: Show all clients (default)
    if (activeTab === 'social-media' && isEmployeePortal) {
      setMyClientsOnly(true);
      setShowAllClients(false);
    } else if (activeTab === 'production') {
      setMyClientsOnly(false);
      setShowAllClients(true); // Production defaults to All Clients
    } else {
      setMyClientsOnly(false);
      setShowAllClients(false);
    }
  }, [activeTab, isEmployeePortal]);

  const clearFilters = () => {
    setSelectedClients([]);
    setSelectedCategories([]);
    setSelectedSubtypes([]);
    setSelectedAssignees([]);
    setMyClientsOnly(false);
    if (activeTab === 'production') {
      setShowAllClients(true); // Keep All Clients as default for Production
    }
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const toggleCategorySelection = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleSubtypeSelection = (subtype) => {
    setSelectedSubtypes(prev =>
      prev.includes(subtype)
        ? prev.filter(s => s !== subtype)
        : [...prev, subtype]
    );
  };

  const toggleAssigneeSelection = (employeeId) => {
    setSelectedAssignees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Set time to noon (12:00) to avoid timezone issues at midnight
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prevMonthLastDay - i, 12, 0, 0), isCurrentMonth: false });
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i, 12, 0, 0), isCurrentMonth: true });
    }
    
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i, 12, 0, 0), isCurrentMonth: false });
    }
    
    return days;
  };

  const getTasksForDate = (date) => {
    const dateStr = formatDateToLocal(date);
    return tasksByDate.get(dateStr) || [];
  };

  // O(1) client lookup map (avoids N×M find() in render loops — was the
  // single biggest cause of the calendar freeze on heavy clients).
  const clientsById = useMemo(() => {
    const m = new Map();
    for (const c of clients) m.set(c._id, c);
    return m;
  }, [clients]);

  // Get client color from task (O(1))
  const getClientColor = useCallback((task) => {
    if (!task?.client_id) return null;
    return clientsById.get(task.client_id)?.color || null;
  }, [clientsById]);

  // Get client name from task (O(1))
  const getClientName = useCallback((task) => {
    if (!task?.client_id) return null;
    return clientsById.get(task.client_id)?.name || null;
  }, [clientsById]);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDayClick = (day) => {
    if (isMobileView) {
      // On mobile, select the day for the detail panel
      setDayDetailDate(day.date);
      return;
    }
    // On desktop: if day has tasks, open preview; otherwise open create task
    const dayTasks = getTasksForDate(day.date);
    if (dayTasks.length > 0) {
      setDayDetailDate(day.date);
    } else {
      setSelectedDate(day.date);
      setSelectedTask(null);
      setShowTaskModal(true);
    }
  };

  const handleTaskClick = (e, task) => {
    e.stopPropagation();
    // Check if user has edit permission before opening modal
    if (task.can_edit === false) {
      toast.error('You do not have permission to edit this task');
      return;
    }
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const handleCreateTask = async (taskData) => {
    try {
      const endpoint = userType === 'admin' ? '/api/admin/tasks' : 
                       userType === 'employee' ? '/api/employee/tasks' : '/api/client/tasks';
      
      if (selectedTask) {
        // Check edit permission before updating
        if (selectedTask.can_edit === false) {
          toast.error('You do not have permission to edit this task');
          return;
        }
        // Optimistic update — merge changes into local state immediately
        setRawTasks(prev => prev.map(t => t._id === selectedTask._id ? { ...t, ...taskData } : t));
        setShowTaskModal(false);
        await api.put(`${endpoint}/${selectedTask._id}`, taskData);
        toast.success('Task updated');
        // Silent background sync for server-computed fields
        fetchTasks({ silent: true });
      } else {
        setShowTaskModal(false);
        await api.post(endpoint, taskData);
        toast.success('Task created');
        // Silent refetch to pick up the new task with its server ID
        fetchTasks({ silent: true });
      }
    } catch (error) {
      console.error('Failed to save task:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save task';
      toast.error(errorMessage);
      // Rollback — re-fetch correct state
      fetchTasks({ silent: true });
    }
  };

  // Handle task deletion from Calendar view
  const handleDeleteTask = async (taskId) => {
    try {
      // Check delete permission
      if (selectedTask && selectedTask.can_delete === false) {
        toast.error('You do not have permission to delete this task');
        return;
      }
      const endpoint = userType === 'admin' ? '/api/admin/tasks' : 
                       userType === 'employee' ? '/api/employee/tasks' : '/api/client/tasks';
      // Optimistic removal — instantly hide the task
      const previousTasks = rawTasks;
      setRawTasks(prev => prev.filter(t => t._id !== taskId));
      setShowTaskModal(false);
      setSelectedTask(null);
      try {
        await api.delete(`${endpoint}/${taskId}`);
        toast.success('Task deleted');
      } catch (error) {
        // Rollback on failure
        setRawTasks(previousTasks);
        throw error;
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete task';
      toast.error(errorMessage);
    }
  };

  // Drag-and-drop handlers for Social Media Calendar
  const canDragTask = (task) => {
    // Admin can drag any task
    if (userType === 'admin') return true;
    // For employees, use the can_edit flag from backend (designation-based permissions)
    if (task.can_edit !== undefined) return task.can_edit;
    // Fallback: only creator can drag (for backward compatibility)
    return task.created_by === currentUserId;
  };

  const handleDragStart = (e, task) => {
    if (!canDragTask(task)) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task._id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetDate) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedTask) return;
    
    const newDateStr = formatDateToLocal(targetDate);
    const currentDate = draggedTask.use_date || draggedTask.due_date;
    
    // Don't update if same date
    if (newDateStr === currentDate) {
      setDraggedTask(null);
      return;
    }
    
    // Show confirmation dialog
    setPendingDragUpdate({
      task: draggedTask,
      newDate: newDateStr,
      targetDate: targetDate
    });
    setShowDragConfirm(true);
    setDraggedTask(null);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
  };

  const confirmDragUpdate = async () => {
    if (!pendingDragUpdate) return;
    
    const { task, newDate } = pendingDragUpdate;
    
    // ── Recalculate due_date to preserve the buffer ─────────────────────
    // 1. If task has stored due_date_sop → derive new due_date via SOP rule.
    // 2. Else (legacy task) → preserve the original (use_date - due_date) gap.
    const newUseDateObj = parseLocalDate(newDate);
    let newDueDateObj;
    if (task.due_date_sop) {
      newDueDateObj = calcDueDateUtil(newUseDateObj, task.due_date_sop, holidays);
    } else if (task.use_date && task.due_date) {
      newDueDateObj = calculateDueDateByGap(newUseDateObj, task.use_date, task.due_date);
    } else {
      newDueDateObj = newUseDateObj; // No buffer info — fall back to same day
    }
    const newDueDateStr = formatDateToLocal(newDueDateObj);
    
    // Optimistic: update both dates instantly
    setRawTasks(prev => prev.map(t => t._id === task._id ? { ...t, use_date: newDate, due_date: newDueDateStr } : t));
    setShowDragConfirm(false);
    setPendingDragUpdate(null);
    
    try {
      const endpoint = userType === 'admin' ? '/api/admin/tasks' : 
                       userType === 'employee' ? '/api/employee/tasks' : '/api/client/tasks';
      
      const updateData = { use_date: newDate, due_date: newDueDateStr };
      await api.put(`${endpoint}/${task._id}`, updateData);
      toast.success(`Task rescheduled — publishes ${newDate}, due ${newDueDateStr}`);
    } catch (error) {
      console.error('Failed to update task date:', error);
      toast.error('Failed to update task date');
      // Rollback — refetch correct positions
      fetchTasks({ silent: true });
    }
  };

  const cancelDragUpdate = () => {
    setShowDragConfirm(false);
    setPendingDragUpdate(null);
  };

  const getSubtypeIcon = (subtype) => {
    const IconComponent = currentConfig.subtypeIcons[subtype] || MoreHorizontal;
    return IconComponent;
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const fullDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  // Memoize the 42-day calendar grid so we don't rebuild Date objects on
  // every render (especially during drag/filter interactions).
  const days = useMemo(() => getDaysInMonth(currentDate), [currentDate]);
  const today = new Date();

  // Get day name from date string (YYYY-MM-DD)
  const getDayName = (dateStr) => {
    if (!dateStr) return '-';
    const date = parseLocalDate(dateStr);
    return fullDayNames[date.getDay()];
  };

  // Get tasks for the current month (for table display)
  // Memoized list of tasks within the current month — used by table,
  // export, summary strip. Recomputing on every render was a major
  // contributor to the calendar freeze.
  const monthTasks = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = `${year}-${String(month + 1).padStart(2, '0')}-${new Date(year, month + 1, 0).getDate()}`;

    return tasks.filter(task => {
      const taskDate = task.use_date || task.due_date;
      return taskDate && taskDate >= firstDay && taskDate <= lastDay;
    }).sort((a, b) => {
      const dateA = a.use_date || a.due_date || '';
      const dateB = b.use_date || b.due_date || '';
      return dateA.localeCompare(dateB);
    });
  }, [tasks, currentDate]);

  const getMonthTasks = useCallback(() => monthTasks, [monthTasks]);

  // Export to branded XLSX/PDF via backend
  const exportCalendar = async (format) => {
    try {
      const params = new URLSearchParams({
        format,
        year: String(currentDate.getFullYear()),
        month: String(currentDate.getMonth() + 1),
        calendar_type: activeTab,
      });
      if (selectedClients.length > 0) params.set('client_ids', selectedClients.join(','));
      if (selectedSubtypes.length > 0) params.set('subtypes', selectedSubtypes.join(','));

      // Pick the endpoint that matches the caller's portal role.
      // Admin → full calendar. Employee → scoped to tasks they can see.
      const endpoint = userType === 'employee'
        ? '/api/employee/calendars/social/export'
        : '/api/admin/calendars/social/export';

      toast.loading(`Generating ${format.toUpperCase()}…`, { id: 'cal-export' });
      const res = await api.get(`${endpoint}?${params}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Filename: "<Client> - <Calendar Type> - <Month Year>.<ext>"
      // Client name appears ONLY when exactly one client filter is active.
      const calendarTypeLabel = activeTab === 'production' ? 'Production Calendar' : 'Social Media Calendar';
      const monthYearLabel = `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
      let clientLabel = '';
      if (selectedClients.length === 1) {
        const c = clients.find(c => c._id === selectedClients[0]);
        if (c?.name) clientLabel = `${c.name} - `;
      }
      const niceName = `${clientLabel}${calendarTypeLabel} - ${monthYearLabel}.${format}`;
      // Strip filesystem-unfriendly characters but preserve spaces.
      link.download = niceName.replace(/[\\/:*?"<>|]/g, '').trim();

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`, { id: 'cal-export' });
    } catch (e) {
      console.error('Calendar export failed', e);
      toast.error(`Export failed: ${e?.response?.data?.detail || e.message}`, { id: 'cal-export' });
    }
  };

  const exportToExcel = () => exportCalendar('xlsx');
  const exportToPdf = () => exportCalendar('pdf');

  const colorClasses = {
    pink: {
      bg: 'bg-pink-500', bgLight: 'bg-pink-50', border: 'border-pink-200',
      text: 'text-pink-600', hover: 'hover:bg-pink-100', gradient: 'from-[#FF3366] to-[#FF3366]'
    },
    blue: {
      bg: 'bg-blue-500', bgLight: 'bg-blue-50', border: 'border-blue-200',
      text: 'text-blue-600', hover: 'hover:bg-blue-100', gradient: 'from-[#3366FF] to-[#3366FF]'
    },
    gray: {
      bg: 'bg-white/400', bgLight: 'bg-white/40', border: 'border-white/40',
      text: 'text-neutral-600', hover: 'hover:bg-neutral-100', gradient: 'from-gray-500 to-gray-600'
    }
  };

  const colors = colorClasses[currentConfig.color] || colorClasses.gray;
  
  // Mobile day names (abbreviated)
  const mobileDayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div className={isMobileView ? 'px-4 py-4' : 'p-8'}>
      {/* Header with Tabs */}
      <div className={`flex items-center justify-between ${isMobileView ? 'mb-4' : 'mb-6'}`}>
        <div>
          <h1 className={`font-bold text-[#1a1a2e] ${isMobileView ? 'text-xl' : 'text-3xl'}`}>Calendars</h1>
          <p className={`text-neutral-600 ${isMobileView ? 'text-sm' : 'mt-1'}`}>Plan and track your content</p>
        </div>
      </div>

      {/* Tab Navigation - Scrollable on mobile */}
      <div className={`mb-4 ${isMobileView ? 'overflow-x-auto -mx-4 px-4' : 'mb-6'}`}>
        <div className={`flex gap-2 ${isMobileView ? 'min-w-max' : 'justify-between'}`}>
          <div className={`flex gap-2 ${isMobileView ? 'min-w-max' : ''}`}>
            <button
              onClick={() => setActiveTab('social-media')}
              className={`flex items-center gap-2 rounded-xl font-medium transition-all ${
                isMobileView ? 'px-3 py-2 text-sm' : 'px-6 py-3'
              } ${
                activeTab === 'social-media'
                  ? 'bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white shadow-lg'
                  : 'glass-card text-neutral-700 hover:bg-white/60'
              }`}
            >
              <Image className={isMobileView ? 'w-4 h-4' : 'w-5 h-5'} />
              {isMobileView ? 'Social Media' : 'Social Media Calendar'}
            </button>
            <button
              onClick={() => setActiveTab('production')}
              className={`flex items-center gap-2 rounded-xl font-medium transition-all ${
                isMobileView ? 'px-3 py-2 text-sm' : 'px-6 py-3'
              } ${
                activeTab === 'production'
                  ? 'bg-gradient-to-r from-[#3366FF] to-[#3366FF] text-white shadow-lg'
                  : 'glass-card text-neutral-700 hover:bg-white/60'
              }`}
            >
              <Video className={isMobileView ? 'w-4 h-4' : 'w-5 h-5'} />
              {isMobileView ? 'Production' : 'Production Calendar'}
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`flex items-center gap-2 rounded-xl font-medium transition-all ${
                isMobileView ? 'px-3 py-2 text-sm' : 'px-6 py-3'
              } ${
                activeTab === 'events'
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg'
                  : 'glass-card text-neutral-700 hover:bg-white/60'
              }`}
            >
              <Music className={isMobileView ? 'w-4 h-4' : 'w-5 h-5'} />
              {isMobileView ? 'Events' : 'Event Calendar'}
            </button>
          </div>
          
          {/* Create SOP Button - For Social Media Calendar, Admin and Employee */}
          {activeTab === 'social-media' && (userType === 'admin' || userType === 'employee') && !isMobileView && (
            <button
              onClick={() => setShowSOPModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg shadow-purple-500/25"
              data-testid="create-sop-btn"
            >
              <FileText className="w-4 h-4" />
              Create SOP
            </button>
          )}
        </div>
      </div>

      {/* Event Calendar Tab */}
      {activeTab === 'events' && (
        <EventCalendar api={api} userType={userType} />
      )}

      {/* Metrics Section - Only for Social Media Calendar */}
      {activeTab === 'social-media' && (
        <div className={`grid gap-3 mb-4 ${isMobileView ? 'grid-cols-2' : 'grid-cols-4 gap-4 mb-6'}`}>
          <div className={`glass-card shadow-soft ${isMobileView ? 'p-3' : 'p-4'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center">
                <CheckSquare className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1a1a2e]">{metrics.total}</p>
                <p className="text-sm text-neutral-600">Total Tasks</p>
              </div>
            </div>
          </div>
          <div className="glass-card shadow-soft p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Film className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1a1a2e]">{metrics.reels}</p>
                <p className="text-sm text-neutral-600">Reels</p>
              </div>
            </div>
          </div>
          <div className="glass-card shadow-soft p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Image className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1a1a2e]">{metrics.posts}</p>
                <p className="text-sm text-neutral-600">Posts</p>
              </div>
            </div>
          </div>
          <div className="glass-card shadow-soft p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <Camera className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1a1a2e]">{metrics.stories}</p>
                <p className="text-sm text-neutral-600">Stories</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Legend - For Social Media Calendar */}
      {activeTab === 'social-media' && (
        <div className="flex flex-wrap items-center gap-4 mb-4 px-1">
          <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Status:</span>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-gray-400" />
              <span className="text-xs text-neutral-600">New</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
              <span className="text-xs text-neutral-600">In Progress</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-purple-500" />
              <span className="text-xs text-neutral-600">Review</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-orange-500" />
              <span className="text-xs text-neutral-600">Revision</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
              <span className="text-xs text-neutral-600">Completed</span>
            </div>
          </div>
        </div>
      )}

      {/* Production Metrics Section */}
      {activeTab === 'production' && !isClientPortal && (
        <div className={`grid gap-3 mb-4 ${isMobileView ? 'grid-cols-2' : 'grid-cols-6 gap-4 mb-6'}`}>
          <div className={`glass-card shadow-soft ${isMobileView ? 'p-3' : 'p-4'}`}>
            <div className="flex items-center gap-2">
              <div className={`bg-blue-100 rounded-xl flex items-center justify-center ${isMobileView ? 'w-8 h-8' : 'w-10 h-10'}`}>
                <Video className={`text-blue-600 ${isMobileView ? 'w-4 h-4' : 'w-5 h-5'}`} />
              </div>
              <div>
                <p className={`font-bold text-[#1a1a2e] ${isMobileView ? 'text-xl' : 'text-2xl'}`}>{productionMetrics.total}</p>
                <p className={`text-neutral-600 ${isMobileView ? 'text-xs' : 'text-sm'}`}>Total</p>
              </div>
            </div>
          </div>
          <div className={`glass-card shadow-soft ${isMobileView ? 'p-3' : 'p-4'}`}>
            <div className="flex items-center gap-2">
              <div className={`bg-purple-100 rounded-xl flex items-center justify-center ${isMobileView ? 'w-8 h-8' : 'w-10 h-10'}`}>
                <CalendarIcon className={`text-purple-600 ${isMobileView ? 'w-4 h-4' : 'w-5 h-5'}`} />
              </div>
              <div>
                <p className={`font-bold text-[#1a1a2e] ${isMobileView ? 'text-xl' : 'text-2xl'}`}>{productionMetrics.calendarShoot}</p>
                <p className={`text-neutral-600 ${isMobileView ? 'text-xs' : 'text-sm'}`}>Calendar</p>
              </div>
            </div>
          </div>
          <div className={`glass-card shadow-soft ${isMobileView ? 'p-3' : 'p-4'}`}>
            <div className="flex items-center gap-2">
              <div className={`bg-orange-100 rounded-xl flex items-center justify-center ${isMobileView ? 'w-8 h-8' : 'w-10 h-10'}`}>
                <Camera className={`text-orange-600 ${isMobileView ? 'w-4 h-4' : 'w-5 h-5'}`} />
              </div>
              <div>
                <p className={`font-bold text-[#1a1a2e] ${isMobileView ? 'text-xl' : 'text-2xl'}`}>{productionMetrics.menuShoot}</p>
                <p className={`text-neutral-600 ${isMobileView ? 'text-xs' : 'text-sm'}`}>Menu</p>
              </div>
            </div>
          </div>
          <div className={`glass-card shadow-soft ${isMobileView ? 'p-3' : 'p-4'}`}>
            <div className="flex items-center gap-2">
              <div className={`bg-red-100 rounded-xl flex items-center justify-center ${isMobileView ? 'w-8 h-8' : 'w-10 h-10'}`}>
                <Video className={`text-red-600 ${isMobileView ? 'w-4 h-4' : 'w-5 h-5'}`} />
              </div>
              <div>
                <p className={`font-bold text-[#1a1a2e] ${isMobileView ? 'text-xl' : 'text-2xl'}`}>{productionMetrics.fpv}</p>
                <p className={`text-neutral-600 ${isMobileView ? 'text-xs' : 'text-sm'}`}>FPV</p>
              </div>
            </div>
          </div>
          {!isMobileView && (
            <>
              <div className="glass-card shadow-soft p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-100 rounded-xl flex items-center justify-center">
                    <Video className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a1a2e]">{productionMetrics.drone}</p>
                    <p className="text-sm text-neutral-600">Drone</p>
                  </div>
                </div>
              </div>
              <div className="glass-card shadow-soft p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-neutral-100 rounded-xl flex items-center justify-center">
                    <MoreHorizontal className="w-5 h-5 text-neutral-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a1a2e]">{productionMetrics.others}</p>
                    <p className="text-sm text-neutral-600">Others</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Production Filters Section */}
      {activeTab === 'production' && !isClientPortal && (
        <div className={`glass-card shadow-soft mb-4 relative z-30 ${isMobileView ? 'p-3' : 'p-4 mb-6'}`}>
          <div className={`flex items-center gap-2 ${isMobileView ? 'flex-wrap' : 'gap-4 flex-wrap'}`}>
            <Filter className={`text-neutral-500 ${isMobileView ? 'w-4 h-4' : 'w-5 h-5'}`} />
            
            {/* Client Filter for Production */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowClientDropdown(!showClientDropdown);
                  setShowCategoryDropdown(false);
                  setShowSubtypeDropdown(false);
                  setShowAssigneeDropdown(false);
                }}
                className="flex items-center gap-2 px-4 py-2 border border-white/40 rounded-xl hover:bg-white/60"
                data-testid="client-filter-trigger"
              >
                <Building className="w-4 h-4 text-neutral-500" />
                <span>
                  {showAllClients && selectedClients.length === 0 && !myClientsOnly 
                    ? 'All Clients' 
                    : myClientsOnly 
                      ? 'My Clients' 
                      : `${selectedClients.length} Client${selectedClients.length > 1 ? 's' : ''}`
                  }
                </span>
              </button>
              {showClientDropdown && (
                <div className="absolute z-50 mt-2 w-72 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-2xl max-h-64 overflow-y-auto">
                  {/* All Clients option */}
                  <label className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-white/30 bg-blue-50/50">
                    <input
                      type="radio"
                      name="clientFilter"
                      checked={showAllClients && selectedClients.length === 0 && !myClientsOnly}
                      onChange={() => {
                        setSelectedClients([]);
                        setMyClientsOnly(false);
                        setShowAllClients(true);
                        setShowClientDropdown(false);
                      }}
                      className="w-4 h-4 text-blue-600 border-blue-300"
                    />
                    <span className="font-semibold text-blue-700">All Clients</span>
                  </label>
                  {/* My Clients option for employees */}
                  {isEmployeePortal && (
                    <label className="flex items-center gap-3 px-4 py-3 hover:bg-purple-50 cursor-pointer border-b border-white/30">
                      <input
                        type="checkbox"
                        checked={myClientsOnly}
                        onChange={(e) => {
                          setMyClientsOnly(e.target.checked);
                          if (e.target.checked) {
                            setSelectedClients([]);
                            setShowAllClients(false);
                          }
                        }}
                        className="w-4 h-4 text-purple-600 rounded border-purple-300"
                      />
                      <span className="font-semibold text-purple-700">My Clients (POC)</span>
                    </label>
                  )}
                  {clients.length === 0 && !isEmployeePortal && (
                    <div className="px-4 py-3 text-sm text-neutral-500">No clients found</div>
                  )}
                  {clients.map(client => (
                    <label key={client._id} className="flex items-center gap-3 px-4 py-2 hover:bg-white/60 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client._id)}
                        onChange={() => {
                          toggleClientSelection(client._id);
                          setMyClientsOnly(false);
                          setShowAllClients(false);
                        }}
                        className="w-4 h-4 text-blue-600 rounded"
                        disabled={myClientsOnly}
                      />
                      <span 
                        className={`w-3 h-3 rounded-full flex-shrink-0 ${myClientsOnly ? 'opacity-50' : ''}`}
                        style={{ backgroundColor: client.color || '#9CA3AF' }}
                      />
                      <span className={myClientsOnly ? 'text-neutral-400' : ''}>{client.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Category Filter for Production */}
            {categories.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => {
                    setShowCategoryDropdown(!showCategoryDropdown);
                    setShowClientDropdown(false);
                    setShowSubtypeDropdown(false);
                    setShowAssigneeDropdown(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-white/40 rounded-xl hover:bg-white/60"
                  data-testid="category-filter-trigger"
                >
                  <Users className="w-4 h-4 text-neutral-500" />
                  <span>Category {selectedCategories.length > 0 ? `(${selectedCategories.length})` : ''}</span>
                </button>
                {showCategoryDropdown && (
                  <div className="absolute z-50 mt-2 w-56 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-2xl max-h-64 overflow-y-auto">
                    {categories.map(category => (
                      <label key={category} className="flex items-center gap-3 px-4 py-2 hover:bg-white/60 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category)}
                          onChange={() => toggleCategorySelection(category)}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span>{category}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Subtype Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowSubtypeDropdown(!showSubtypeDropdown);
                  setShowClientDropdown(false);
                  setShowCategoryDropdown(false);
                  setShowAssigneeDropdown(false);
                }}
                className="flex items-center gap-2 px-4 py-2 border border-white/40 rounded-xl hover:bg-white/60"
                data-testid="subtype-filter-trigger"
              >
                <BarChart3 className="w-4 h-4 text-neutral-500" />
                <span>Subtype {selectedSubtypes.length > 0 ? `(${selectedSubtypes.length})` : ''}</span>
              </button>
              {showSubtypeDropdown && (
                <div className="absolute z-50 mt-2 w-48 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-2xl">
                  {currentConfig.subtypes.map(subtype => {
                    const Icon = currentConfig.subtypeIcons[subtype];
                    return (
                      <label key={subtype} className="flex items-center gap-3 px-4 py-2 hover:bg-white/60 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedSubtypes.includes(subtype)}
                          onChange={() => toggleSubtypeSelection(subtype)}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <Icon className="w-4 h-4 text-neutral-500" />
                        <span>{subtype}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Assignee Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowAssigneeDropdown(!showAssigneeDropdown);
                  setShowClientDropdown(false);
                  setShowCategoryDropdown(false);
                  setShowSubtypeDropdown(false);
                }}
                className="flex items-center gap-2 px-4 py-2 border border-white/40 rounded-xl hover:bg-white/60"
                data-testid="assignee-filter-trigger"
              >
                <User className="w-4 h-4 text-neutral-500" />
                <span>Assignee {selectedAssignees.length > 0 ? `(${selectedAssignees.length})` : ''}</span>
              </button>
              {showAssigneeDropdown && (
                <div className="absolute z-50 mt-2 w-64 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-2xl max-h-64 overflow-y-auto">
                  {employees.length === 0 && (
                    <div className="px-4 py-3 text-sm text-neutral-500">No team members found</div>
                  )}
                  {employees.map(emp => (
                    <label key={emp._id} className="flex items-center gap-3 px-4 py-2 hover:bg-white/60 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedAssignees.includes(emp._id)}
                        onChange={() => toggleAssigneeSelection(emp._id)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span>{emp.personal?.full_name || emp.work_email || 'Unknown'}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Clear Button */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-neutral-600 hover:text-[#1a1a2e] border border-white/40 rounded-xl"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Selected filters display for production */}
          {(selectedClients.length > 0 || selectedCategories.length > 0 || selectedSubtypes.length > 0 || selectedAssignees.length > 0 || myClientsOnly) && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/30">
              {myClientsOnly && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                  My Clients
                  <button onClick={() => setMyClientsOnly(false)} className="ml-1 hover:text-purple-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedClients.map(clientId => {
                const client = clients.find(c => c._id === clientId);
                return (
                  <span 
                    key={clientId} 
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium"
                    style={{
                      backgroundColor: client?.color ? getLighterColor(client.color, 0.2) : '#DBEAFE',
                      color: client?.color || '#1D4ED8',
                      borderLeft: `3px solid ${client?.color || '#3B82F6'}`
                    }}
                  >
                    <span 
                      className="w-2 h-2 rounded-full" 
                      style={{ backgroundColor: client?.color || '#3B82F6' }}
                    />
                    {client?.name || clientId}
                    <button onClick={() => toggleClientSelection(clientId)} className="ml-1 hover:opacity-70">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
              {selectedCategories.map(cat => (
                <span key={cat} className="inline-flex items-center gap-1 px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm">
                  {cat}
                  <button onClick={() => toggleCategorySelection(cat)} className="ml-1 hover:text-cyan-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedSubtypes.map(sub => (
                <span key={sub} className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                  {sub}
                  <button onClick={() => toggleSubtypeSelection(sub)} className="ml-1 hover:text-orange-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedAssignees.map(assigneeId => {
                const emp = employees.find(e => e._id === assigneeId);
                return (
                  <span key={assigneeId} className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                    <User className="w-3 h-3" />
                    {emp?.personal?.full_name || emp?.work_email || 'Unknown'}
                    <button onClick={() => toggleAssigneeSelection(assigneeId)} className="ml-1 hover:text-green-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Social Media Filters Section */}
      {activeTab === 'social-media' && !isClientPortal && (
        <div className="glass-card p-4 mb-6 shadow-soft relative z-30">
          <div className="flex items-center gap-4 flex-wrap">
            <Filter className="w-5 h-5 text-neutral-500" />
            
            {/* Client Filter - Not for client portal */}
            {!isClientPortal && (
              <div className="relative">
                <button
                  onClick={() => {
                    setShowClientDropdown(!showClientDropdown);
                    setShowCategoryDropdown(false);
                    setShowSubtypeDropdown(false);
                    setShowAssigneeDropdown(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-white/40 rounded-xl hover:bg-white/60"
                  data-testid="client-filter-trigger"
                >
                  <Building className="w-4 h-4 text-neutral-500" />
                  <span>Client {selectedClients.length > 0 ? `(${selectedClients.length})` : ''}</span>
                </button>
                {showClientDropdown && (
                  <div className="absolute z-50 mt-2 w-72 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-2xl max-h-64 overflow-y-auto">
                    {/* My Clients option for employees - Always show first */}
                    {isEmployeePortal && (
                      <label className="flex items-center gap-3 px-4 py-3 hover:bg-purple-50 cursor-pointer border-b border-white/30 bg-purple-25">
                        <input
                          type="checkbox"
                          checked={myClientsOnly}
                          onChange={(e) => {
                            setMyClientsOnly(e.target.checked);
                            if (e.target.checked) {
                              setSelectedClients([]);
                            }
                          }}
                          className="w-4 h-4 text-purple-600 rounded border-purple-300"
                        />
                        <span className="font-semibold text-purple-700">My Clients (POC)</span>
                      </label>
                    )}
                    {clients.length === 0 && !isEmployeePortal && (
                      <div className="px-4 py-3 text-sm text-neutral-500">No clients found</div>
                    )}
                    {clients.map(client => (
                      <label key={client._id} className="flex items-center gap-3 px-4 py-2 hover:bg-white/60 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedClients.includes(client._id)}
                          onChange={() => {
                            toggleClientSelection(client._id);
                            setMyClientsOnly(false);
                          }}
                          className="w-4 h-4 text-pink-600 rounded"
                          disabled={myClientsOnly}
                        />
                        <span 
                          className={`w-3 h-3 rounded-full flex-shrink-0 ${myClientsOnly ? 'opacity-50' : ''}`}
                          style={{ backgroundColor: client.color || '#9CA3AF' }}
                        />
                        <span className={myClientsOnly ? 'text-neutral-400' : ''}>{client.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Category Filter - Not for client portal */}
            {!isClientPortal && categories.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => {
                    setShowCategoryDropdown(!showCategoryDropdown);
                    setShowClientDropdown(false);
                    setShowSubtypeDropdown(false);
                    setShowAssigneeDropdown(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-white/40 rounded-xl hover:bg-white/60"
                  data-testid="category-filter-trigger"
                >
                  <Users className="w-4 h-4 text-neutral-500" />
                  <span>Category {selectedCategories.length > 0 ? `(${selectedCategories.length})` : ''}</span>
                </button>
                {showCategoryDropdown && (
                  <div className="absolute z-50 mt-2 w-56 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-2xl max-h-64 overflow-y-auto">
                    {categories.map(category => (
                      <label key={category} className="flex items-center gap-3 px-4 py-2 hover:bg-white/60 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category)}
                          onChange={() => toggleCategorySelection(category)}
                          className="w-4 h-4 text-pink-600 rounded"
                        />
                        <span>{category}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Subtype Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowSubtypeDropdown(!showSubtypeDropdown);
                  setShowClientDropdown(false);
                  setShowCategoryDropdown(false);
                  setShowAssigneeDropdown(false);
                }}
                className="flex items-center gap-2 px-4 py-2 border border-white/40 rounded-xl hover:bg-white/60"
                data-testid="subtype-filter-trigger"
              >
                <BarChart3 className="w-4 h-4 text-neutral-500" />
                <span>Subtype {selectedSubtypes.length > 0 ? `(${selectedSubtypes.length})` : ''}</span>
              </button>
              {showSubtypeDropdown && (
                <div className="absolute z-50 mt-2 w-48 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-2xl">
                  {currentConfig.subtypes.map(subtype => {
                    const Icon = currentConfig.subtypeIcons[subtype];
                    return (
                      <label key={subtype} className="flex items-center gap-3 px-4 py-2 hover:bg-white/60 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedSubtypes.includes(subtype)}
                          onChange={() => toggleSubtypeSelection(subtype)}
                          className="w-4 h-4 text-pink-600 rounded"
                        />
                        <Icon className="w-4 h-4 text-neutral-500" />
                        <span>{subtype}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Assignee Filter - Not for client portal */}
            {!isClientPortal && (
              <div className="relative">
                <button
                  onClick={() => {
                    setShowAssigneeDropdown(!showAssigneeDropdown);
                    setShowClientDropdown(false);
                    setShowCategoryDropdown(false);
                    setShowSubtypeDropdown(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-white/40 rounded-xl hover:bg-white/60"
                >
                  <User className="w-4 h-4 text-neutral-500" />
                  <span>Assignee {selectedAssignees.length > 0 ? `(${selectedAssignees.length})` : ''}</span>
                </button>
                {showAssigneeDropdown && (
                  <div className="absolute z-50 mt-2 w-64 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-2xl max-h-64 overflow-y-auto">
                    {employees.length === 0 && (
                      <div className="px-4 py-3 text-sm text-neutral-500">No team members found</div>
                    )}
                    {employees.map(emp => (
                      <label key={emp._id} className="flex items-center gap-3 px-4 py-2 hover:bg-white/60 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedAssignees.includes(emp._id)}
                          onChange={() => toggleAssigneeSelection(emp._id)}
                          className="w-4 h-4 text-pink-600 rounded"
                        />
                        <span>{emp.personal?.full_name || emp.work_email || 'Unknown'}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Clear Button - Not for client portal */}
            {!isClientPortal && hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-neutral-600 hover:text-[#1a1a2e] border border-white/40 rounded-xl"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Selected filters display */}
          {(selectedClients.length > 0 || selectedCategories.length > 0 || selectedSubtypes.length > 0 || selectedAssignees.length > 0 || myClientsOnly) && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/30">
              {myClientsOnly && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                  My Clients
                  <button onClick={() => setMyClientsOnly(false)} className="ml-1 hover:text-purple-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedClients.map(clientId => {
                const client = clients.find(c => c._id === clientId);
                return (
                  <span 
                    key={clientId} 
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium"
                    style={{
                      backgroundColor: client?.color ? getLighterColor(client.color, 0.2) : '#FCE7F3',
                      color: client?.color || '#BE185D',
                      borderLeft: `3px solid ${client?.color || '#EC4899'}`
                    }}
                  >
                    <span 
                      className="w-2 h-2 rounded-full" 
                      style={{ backgroundColor: client?.color || '#EC4899' }}
                    />
                    {client?.name || clientId}
                    <button onClick={() => toggleClientSelection(clientId)} className="ml-1 hover:opacity-70">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
              {selectedCategories.map(cat => (
                <span key={cat} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                  {cat}
                  <button onClick={() => toggleCategorySelection(cat)} className="ml-1 hover:text-blue-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedSubtypes.map(sub => (
                <span key={sub} className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                  {sub}
                  <button onClick={() => toggleSubtypeSelection(sub)} className="ml-1 hover:text-orange-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedAssignees.map(assigneeId => {
                const emp = employees.find(e => e._id === assigneeId);
                return (
                  <span key={assigneeId} className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                    <User className="w-3 h-3" />
                    {emp?.personal?.full_name || emp?.work_email || 'Unknown'}
                    <button onClick={() => toggleAssigneeSelection(assigneeId)} className="ml-1 hover:text-green-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Calendar - Only for Social Media and Production tabs */}
      {(activeTab === 'social-media' || activeTab === 'production') && (
      <div className="glass-card shadow-soft overflow-hidden">
        {/* Calendar Header */}
        <div className={`px-6 py-4 bg-gradient-to-r ${colors.gradient} text-white flex items-center justify-between`}>
          <button onClick={handlePrevMonth} className="p-2 rounded-xl hover:bg-white/20 transition-colors" data-testid="calendar-prev-month">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            {currentDate.getMonth() !== new Date().getMonth() || currentDate.getFullYear() !== new Date().getFullYear() ? (
              <button 
                onClick={() => setCurrentDate(new Date())} 
                className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-sm font-medium transition-colors"
                data-testid="calendar-today-btn"
              >
                Today
              </button>
            ) : null}
          </div>
          <button onClick={handleNextMonth} className="p-2 rounded-xl hover:bg-white/20 transition-colors" data-testid="calendar-next-month">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Day Headers - Desktop only */}
        {!isMobileView && (
        <div className="grid grid-cols-7 border-b border-white/40">
          {dayNames.map(day => (
            <div key={day} className="py-3 text-center text-sm font-medium text-neutral-600 bg-white/40">
              {day}
            </div>
          ))}
        </div>
        )}

        {/* Mobile Day Headers — compact */}
        {isMobileView && (
        <div className="grid grid-cols-7">
          {['S','M','T','W','T','F','S'].map((d, i) => (
            <div key={i} className="py-2 text-center text-[10px] font-semibold text-neutral-500 uppercase">
              {d}
            </div>
          ))}
        </div>
        )}

        {/* Calendar Grid - Always visible */}
        {loading ? (
          <div className="h-96 flex items-center justify-center">
            <Loader2 className={`w-8 h-8 animate-spin ${colors.text}`} />
          </div>
        ) : (
          <>
            {/* Info banner when no filters selected */}
            {!hasActiveFilters && !isClientPortal && (
              <div className={`px-4 py-2 ${colors.bgLight} ${colors.text} text-sm flex items-center gap-2 border-b border-white/40`}>
                <Filter className="w-4 h-4" />
                <span>Select filters above to view tasks. Click on any date to create a new task.</span>
              </div>
            )}
            {/* Drag hint for social media calendar */}
            {activeTab === 'social-media' && hasActiveFilters && (
              <div className="px-4 py-2 bg-blue-50 text-blue-700 text-sm flex items-center gap-2 border-b border-white/40">
                <span>Tip: Drag and drop tasks to change their Use Date</span>
              </div>
            )}
            {/* Calendar Grid View */}
            {(!isMobileView || calendarViewMode === 'grid') && !isMobileView && (
            <div className="grid grid-cols-7">
              {days.map((day, index) => {
                const dayTasks = hasActiveFilters || isClientPortal ? getTasksForDate(day.date) : [];
                const isToday = day.date.toDateString() === today.toDateString();
                
                return (
                  <div
                    key={index}
                    onClick={() => handleDayClick(day)}
                    onDragOver={activeTab === 'social-media' ? handleDragOver : undefined}
                    onDrop={activeTab === 'social-media' ? (e) => handleDrop(e, day.date) : undefined}
                    className={`min-h-[120px] p-2 border-b border-r border-white/30 cursor-pointer transition-colors ${
                      day.isCurrentMonth ? 'bg-white hover:bg-white/60' : 'bg-white/40'
                    } ${draggedTask ? 'hover:bg-blue-50' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className={`text-sm font-medium ${
                        isToday 
                          ? `w-7 h-7 rounded-full ${colors.bg} text-white flex items-center justify-center`
                          : day.isCurrentMonth ? 'text-[#1a1a2e]' : 'text-neutral-400'
                      }`}>
                        {day.date.getDate()}
                      </div>
                      {dayTasks.length > 0 && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${colors.bgLight} ${colors.text}`} data-testid={`day-count-${formatDateToLocal(day.date)}`}>
                          {dayTasks.length}
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-1">
                      {dayTasks.slice(0, 3).map((task, taskIndex) => {
                        const SubtypeIcon = getSubtypeIcon(task.task_subtype);
                        const clientColor = getClientColor(task);
                        const clientName = getClientName(task);
                        const isDraggable = activeTab === 'social-media' && canDragTask(task);
                        
                        return (
                          <div
                            key={taskIndex}
                            onClick={(e) => handleTaskClick(e, task)}
                            draggable={isDraggable}
                            onDragStart={isDraggable ? (e) => handleDragStart(e, task) : undefined}
                            onDragEnd={isDraggable ? handleDragEnd : undefined}
                            className={`text-xs px-2 py-1.5 rounded-xl truncate flex items-center gap-1.5 cursor-pointer transition-all hover:shadow-soft ${
                              isDraggable ? 'cursor-grab active:cursor-grabbing' : ''
                            } ${draggedTask?._id === task._id ? 'opacity-50' : ''}`}
                            style={{
                              backgroundColor: clientColor ? getLighterColor(clientColor, 0.15) : colors.bgLight.includes('pink') ? 'rgba(236, 72, 153, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                              borderLeft: `3px solid ${clientColor || (colors.bg.includes('pink') ? '#EC4899' : '#3B82F6')}`,
                              color: clientColor || (colors.text.includes('pink') ? '#DB2777' : '#2563EB')
                            }}
                            title={`${task.title}${clientName ? ` - ${clientName}` : ''}${isDraggable ? ' (drag to move)' : ''}`}
                            data-testid={`calendar-task-${task._id}`}
                          >
                            <SubtypeIcon className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate font-medium">{task.title}</span>
                          </div>
                        );
                      })}
                      {dayTasks.length > 3 && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); setDayDetailDate(day.date); }}
                          className={`text-xs ${colors.text} font-medium px-2 hover:underline cursor-pointer`}
                          data-testid={`day-more-${formatDateToLocal(day.date)}`}
                        >
                          +{dayTasks.length - 3} more
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            )}

            {/* Mobile Calendar — Google Calendar inspired */}
            {isMobileView && (
              <>
                {/* Compact Mini-Calendar */}
                <div className="grid grid-cols-7 gap-0">
                  {days.map((day, index) => {
                    const dayTasks = hasActiveFilters || isClientPortal ? getTasksForDate(day.date) : [];
                    const isToday = day.date.toDateString() === today.toDateString();
                    const isSelected = dayDetailDate && day.date.toDateString() === dayDetailDate.toDateString();
                    
                    return (
                      <div
                        key={index}
                        onClick={() => handleDayClick(day)}
                        className={`flex flex-col items-center py-2 cursor-pointer transition-colors border-b border-white/20 ${
                          !day.isCurrentMonth ? 'opacity-30' : ''
                        } ${isSelected ? `${colors.bgLight}` : 'hover:bg-white/40'}`}
                        data-testid={`mobile-day-${formatDateToLocal(day.date)}`}
                      >
                        <span className={`text-sm font-semibold leading-none ${
                          isToday 
                            ? `w-7 h-7 rounded-full ${colors.bg} text-white flex items-center justify-center`
                            : isSelected 
                              ? `w-7 h-7 rounded-full bg-white dark:bg-white/10 ${colors.text} flex items-center justify-center font-bold`
                              : 'text-[#1a1a2e] py-1'
                        }`}>
                          {day.date.getDate()}
                        </span>
                        {/* Dot indicators */}
                        {dayTasks.length > 0 && day.isCurrentMonth && (
                          <div className="flex items-center gap-0.5 mt-1 h-2">
                            {dayTasks.length <= 3 ? (
                              dayTasks.slice(0, 3).map((t, i) => (
                                <div key={i} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: getClientColor(t) || (colors.bg.includes('pink') ? '#EC4899' : '#3B82F6') }} />
                              ))
                            ) : (
                              <>
                                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: getClientColor(dayTasks[0]) || (colors.bg.includes('pink') ? '#EC4899' : '#3B82F6') }} />
                                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: getClientColor(dayTasks[1]) || (colors.bg.includes('pink') ? '#EC4899' : '#3B82F6') }} />
                                <span className={`text-[8px] font-bold ${colors.text} leading-none`}>+{dayTasks.length - 2}</span>
                              </>
                            )}
                          </div>
                        )}
                        {(dayTasks.length === 0 || !day.isCurrentMonth) && <div className="h-2 mt-1" />}
                      </div>
                    );
                  })}
                </div>

                {/* Selected Day Detail Panel */}
                <div className="border-t-2 border-white/40 dark:border-white/10">
                  {dayDetailDate ? (
                    <div>
                      <div className={`flex items-center justify-between px-4 py-3 ${colors.bgLight}`}>
                        <div className="flex items-center gap-2">
                          <span className={`text-lg font-bold ${colors.text}`}>
                            {dayDetailDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                          {(() => {
                            const tasks = getTasksForDate(dayDetailDate);
                            return tasks.length > 0 ? (
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.bg} text-white`}>
                                {tasks.length}
                              </span>
                            ) : null;
                          })()}
                        </div>
                        <button
                          onClick={() => { setSelectedDate(dayDetailDate); setSelectedTask(null); setShowTaskModal(true); }}
                          className={`p-1.5 rounded-lg ${colors.bg} text-white`}
                          data-testid="mobile-create-task-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Task list for selected day */}
                      <div className="max-h-[40vh] overflow-y-auto">
                        {getTasksForDate(dayDetailDate).length > 0 ? (
                          <div className="divide-y divide-white/20 dark:divide-white/5">
                            {getTasksForDate(dayDetailDate).map((task, i) => {
                              const SubtypeIcon = getSubtypeIcon(task.task_subtype);
                              const clientColor = getClientColor(task);
                              const clientName = getClientName(task);
                              return (
                                <div
                                  key={task._id || i}
                                  onClick={(e) => handleTaskClick(e, task)}
                                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/40 dark:hover:bg-white/5 transition-colors active:bg-white/60"
                                  data-testid={`mobile-task-${task._id}`}
                                >
                                  <div className="w-1 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: clientColor || (colors.bg.includes('pink') ? '#EC4899' : '#3B82F6') }} />
                                  <SubtypeIcon className="w-4 h-4 flex-shrink-0 text-neutral-500" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-[#1a1a2e] truncate">{task.title}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      {clientName && <span className="text-xs text-neutral-500 truncate">{clientName}</span>}
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                      task.status === 'completed' ? 'bg-green-100 text-green-700' :
                                      task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                      task.status === 'review' ? 'bg-purple-100 text-purple-700' :
                                      task.status === 'revision' ? 'bg-orange-100 text-orange-700' :
                                      'bg-neutral-100 text-neutral-700'
                                    }`}>
                                      {task.status?.replace('_', ' ') || 'New'}
                                    </span>
                                    {task.task_subtype && <span className="text-[9px] text-neutral-400">{task.task_subtype}</span>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center py-10 text-neutral-400">
                            <CalendarIcon className="w-8 h-8 mb-2 opacity-40" />
                            <p className="text-sm">No tasks for this day</p>
                            <button
                              onClick={() => { setSelectedDate(dayDetailDate); setSelectedTask(null); setShowTaskModal(true); }}
                              className={`mt-3 text-xs font-medium ${colors.text} hover:underline`}
                            >
                              + Create a task
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 text-neutral-400">
                      <CalendarIcon className="w-8 h-8 mb-2 opacity-40" />
                      <p className="text-sm">Tap a day to see tasks</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
      )}

      {/* Legend - Only for Social Media and Production tabs */}
      {(activeTab === 'social-media' || activeTab === 'production') && (
      <div className={`mt-4 flex items-center gap-6 text-sm ${colors.text}`}>
        <span className="font-medium">Subtypes:</span>
        {Object.entries(currentConfig.subtypeIcons).map(([name, Icon]) => (
          <div key={name} className="flex items-center gap-1">
            <Icon className="w-4 h-4" />
            <span>{name}</span>
          </div>
        ))}
      </div>
      )}

      {/* Social Media Calendar Table Section */}
      {activeTab === 'social-media' && (
        <div className={`mt-6 glass-card shadow-soft overflow-hidden ${isMobileView ? '' : ''}`}>
          {/* Table Header */}
          <div className={`flex items-center justify-between ${isMobileView ? 'p-3' : 'p-4'} border-b border-white/40 bg-pink-50 dark:bg-transparent`}>
            <div className="flex items-center gap-2">
              <Table className="w-5 h-5 text-pink-600" />
              <h3 className={`font-semibold text-[#1a1a2e] ${isMobileView ? 'text-sm' : 'text-lg'}`}>
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()} Tasks
              </h3>
              <span className="px-2 py-0.5 bg-pink-100 text-pink-700 text-xs font-medium rounded-full">
                {getMonthTasks().length} tasks
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={exportToExcel}
                className={`flex items-center gap-2 ${isMobileView ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'} bg-pink-600 text-white rounded-xl hover:bg-pink-700 transition-colors font-medium shadow-sm`}
                data-testid="export-excel-btn"
                title="Download branded Excel"
              >
                <Download className={isMobileView ? 'w-4 h-4' : 'w-4 h-4'} />
                {!isMobileView && 'Excel'}
              </button>
              <button
                onClick={exportToPdf}
                className={`flex items-center gap-2 ${isMobileView ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'} bg-white border border-pink-200 text-pink-700 rounded-xl hover:bg-pink-50 transition-colors font-medium shadow-sm`}
                data-testid="export-pdf-btn"
                title="Download branded PDF"
              >
                <FileText className={isMobileView ? 'w-4 h-4' : 'w-4 h-4'} />
                {!isMobileView && 'PDF'}
              </button>
            </div>
          </div>

          {/* Table Content */}
          {getMonthTasks().length === 0 ? (
            <div className={`flex flex-col items-center justify-center ${isMobileView ? 'py-8' : 'py-12'} text-neutral-500`}>
              <CalendarIcon className="w-12 h-12 mb-3 text-neutral-300" />
              <p className="font-medium">No tasks for {monthNames[currentDate.getMonth()]}</p>
              <p className="text-sm text-neutral-400">Click on a date above to create a task</p>
            </div>
          ) : isMobileView ? (
            /* Mobile Card View */
            <div className="divide-y divide-white/30 max-h-96 overflow-y-auto">
              {getMonthTasks().map((task, index) => {
                const useDate = task.use_date || task.due_date || '';
                const SubtypeIcon = getSubtypeIcon(task.task_subtype);
                const clientColor = getClientColor(task);
                const clientName = getClientName(task);
                return (
                  <div 
                    key={task._id || index} 
                    className="p-3 hover:bg-white/60 cursor-pointer"
                    onClick={() => {
                      setSelectedTask(task);
                      setShowTaskModal(true);
                    }}
                    data-testid={`task-row-mobile-${index}`}
                    style={{ borderLeft: clientColor ? `4px solid ${clientColor}` : '4px solid #E5E7EB' }}
                  >
                    {/* Client Name */}
                    {clientName && (
                      <div className="flex items-center gap-2 mb-2">
                        <span 
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: clientColor || '#9CA3AF' }}
                        />
                        <span className="text-xs font-semibold text-neutral-700">{clientName}</span>
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#1a1a2e]">{formatDisplayDate(useDate)}</span>
                        <span className="text-xs text-neutral-500">{getDayName(useDate)}</span>
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex items-center gap-1 ${
                        task.status === 'Completed' ? 'bg-green-100 text-green-700' :
                        task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                        task.status === 'Review' ? 'bg-purple-100 text-purple-700' :
                        task.status === 'Revision' ? 'bg-orange-100 text-orange-700' :
                        'bg-neutral-100 text-neutral-700'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          task.status === 'Completed' ? 'bg-green-500' :
                          task.status === 'In Progress' ? 'bg-blue-500' :
                          task.status === 'Review' ? 'bg-purple-500' :
                          task.status === 'Revision' ? 'bg-orange-500' :
                          'bg-gray-400'
                        }`} />
                        {task.status || 'New'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <SubtypeIcon className="w-4 h-4 text-pink-500" />
                      <span className="text-xs text-pink-600 font-medium">{task.task_subtype || '-'}</span>
                    </div>
                    <p className="font-medium text-[#1a1a2e] text-sm mb-1">{task.title}</p>
                    {task.description && (
                      <p className="text-xs text-neutral-500 line-clamp-2">{task.description}</p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop Table View */
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full" data-testid="social-media-tasks-table">
                <thead className="bg-white/40 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">Use Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">Use Day</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">Subtype</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider max-w-xs">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">References/URLs</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider min-w-[110px]">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {getMonthTasks().map((task, index) => {
                    const useDate = task.use_date || task.due_date || '';
                    const SubtypeIcon = getSubtypeIcon(task.task_subtype);
                    const clientColor = getClientColor(task);
                    const clientName = getClientName(task);
                    return (
                      <tr 
                        key={task._id || index} 
                        className="hover:bg-pink-50 cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedTask(task);
                          setShowTaskModal(true);
                        }}
                        data-testid={`task-row-${index}`}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span 
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: clientColor || '#9CA3AF' }}
                            />
                            <span className="text-sm font-medium text-[#1a1a2e]">{clientName || '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#1a1a2e] whitespace-nowrap">
                          {formatDisplayDate(useDate)}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-600 whitespace-nowrap">
                          {getDayName(useDate)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <SubtypeIcon className="w-4 h-4 text-pink-500" />
                            <span className="text-sm font-medium text-pink-600">{task.task_subtype || '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-[#1a1a2e] max-w-xs truncate">
                          {task.title}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-500 max-w-xs">
                          <span className="line-clamp-2">{task.description || '-'}</span>
                        </td>
                        <td className="px-4 py-3 text-sm max-w-xs">
                          {task.references ? (
                            <div className="flex flex-wrap gap-1">
                              {extractUrlsFromText(task.references).slice(0, 2).map((url, idx) => (
                                <a
                                  key={idx}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded text-xs font-medium transition-colors"
                                  title={url}
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  <span className="truncate max-w-[80px]">
                                    {(() => {
                                      try {
                                        return new URL(url).hostname.replace('www.', '');
                                      } catch {
                                        return url.substring(0, 15);
                                      }
                                    })()}
                                  </span>
                                </a>
                              ))}
                              {extractUrlsFromText(task.references).length > 2 && (
                                <span className="text-xs text-neutral-400">+{extractUrlsFromText(task.references).length - 2}</span>
                              )}
                              {extractUrlsFromText(task.references).length === 0 && (
                                <span className="text-neutral-400 text-xs line-clamp-1">{task.references.substring(0, 30)}...</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-neutral-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap min-w-[110px]">
                          <span className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full ${
                            task.status === 'Completed' ? 'bg-green-100 text-green-700' :
                            task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                            task.status === 'Review' ? 'bg-purple-100 text-purple-700' :
                            task.status === 'Revision' ? 'bg-orange-100 text-orange-700' :
                            'bg-neutral-100 text-neutral-700'
                          }`}>
                            {task.status || 'New'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Instagram Grid Preview - Social Media Calendar */}
      {activeTab === 'social-media' && getMonthTasks().length > 0 && (
        <div className="mt-6 glass-card shadow-soft overflow-hidden">
          {/* Grid Header */}
          <div className={`flex items-center justify-between ${isMobileView ? 'p-3' : 'p-4'} border-b border-white/40 bg-purple-50 dark:bg-transparent`}>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <h3 className={`font-semibold text-[#1a1a2e] ${isMobileView ? 'text-sm' : 'text-lg'}`}>
                Instagram Grid Preview
              </h3>
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                {getMonthTasks().filter(t => t.task_subtype === 'Post' || t.task_subtype === 'Reel').length} posts
              </span>
              <span className="px-2 py-0.5 bg-neutral-100 text-neutral-500 text-xs font-medium rounded-full">
                Posts & Reels only
              </span>
            </div>
          </div>

          {/* Instagram Grid - Sorted by use_date descending (newest first, like Instagram) */}
          <div className={`${isMobileView ? 'p-2' : 'p-4'} bg-gray-50 dark:bg-transparent`}>
            <div className={`grid grid-cols-3 ${isMobileView ? 'gap-1' : 'gap-3'}`}>
              {getMonthTasks()
                // Filter: Only Posts and Reels (no Stories)
                .filter(task => task.task_subtype === 'Post' || task.task_subtype === 'Reel')
                // Sort by use_date descending (newest first) - Instagram shows newest at top-left
                .sort((a, b) => {
                  const dateA = new Date((a.use_date || a.due_date || '1970-01-01') + 'T00:00:00');
                  const dateB = new Date((b.use_date || b.due_date || '1970-01-01') + 'T00:00:00');
                  return dateB - dateA; // Descending: newest first
                })
                .map((task, index) => {
                const useDate = task.use_date || task.due_date || '';
                const clientColor = getClientColor(task);
                const SubtypeIcon = getSubtypeIcon(task.task_subtype);
                const dateObj = useDate ? new Date(useDate + 'T00:00:00') : null;
                const dayName = dateObj ? dateObj.toLocaleDateString('en-US', { weekday: 'short' }) : '';
                const dateFormatted = dateObj ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                
                return (
                  <div
                    key={task._id || index}
                    onClick={() => {
                      setSelectedTask(task);
                      setShowTaskModal(true);
                    }}
                    className={`
                      aspect-square rounded-xl border-2 cursor-pointer
                      transition-all duration-200 hover:scale-[1.02] hover:shadow-lg
                      flex flex-col items-center justify-center text-center relative
                      ${isMobileView ? 'p-1.5' : 'p-3'}
                      ${task.status === 'Completed' 
                        ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:border-green-400' 
                        : task.status === 'In Progress'
                        ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:border-blue-400'
                        : task.status === 'Review'
                        ? 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:border-purple-400'
                        : task.status === 'Revision'
                        ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:border-orange-400'
                        : 'bg-gradient-to-br from-gray-50 to-white border-white/40 hover:border-pink-400'
                      }
                    `}
                    data-testid={`grid-cell-${index}`}
                    style={{ 
                      boxShadow: clientColor ? `inset 0 -3px 0 ${clientColor}` : 'none'
                    }}
                  >
                    {/* Status Indicator Dot - Top Right */}
                    <span 
                      className={`absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${
                        task.status === 'Completed' ? 'bg-green-500' :
                        task.status === 'In Progress' ? 'bg-blue-500' :
                        task.status === 'Review' ? 'bg-purple-500' :
                        task.status === 'Revision' ? 'bg-orange-500' :
                        'bg-gray-400'
                      }`}
                      title={task.status || 'New'}
                    />
                    
                    {/* Subtype Icon */}
                    <SubtypeIcon className={`${isMobileView ? 'w-4 h-4' : 'w-6 h-6'} text-pink-500 mb-1`} />
                    
                    {/* Task Title */}
                    <p className={`font-semibold text-[#1a1a2e] ${isMobileView ? 'text-[10px] leading-tight' : 'text-sm'} line-clamp-2 mb-1`}>
                      {task.title}
                    </p>
                    
                    {/* Date */}
                    <p className={`font-medium ${isMobileView ? 'text-[10px]' : 'text-xs'} text-neutral-700`}>
                      {dateFormatted}
                    </p>
                    
                    {/* Day */}
                    <p className={`${isMobileView ? 'text-[9px]' : 'text-xs'} text-neutral-500`}>
                      {dayName}
                    </p>
                    
                    {/* Status Badge - Only on desktop */}
                    {!isMobileView && (
                      <span className={`mt-1.5 px-2 py-0.5 text-[10px] font-medium rounded-full ${
                        task.status === 'Completed' ? 'bg-green-200 text-green-800' :
                        task.status === 'In Progress' ? 'bg-blue-200 text-blue-800' :
                        task.status === 'Review' ? 'bg-purple-200 text-purple-800' :
                        'bg-neutral-200 text-neutral-700'
                      }`}>
                        {task.status || 'New'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <TaskModal
          isOpen={showTaskModal}
          onClose={() => {
            setShowTaskModal(false);
            setSelectedTask(null);
            setSelectedDate(null);
          }}
          onSubmit={handleCreateTask}
          onDelete={handleDeleteTask}
          existingTask={selectedTask}
          api={api}
          userType={userType}
          lockedTaskType={currentConfig.taskType}
          defaultDate={selectedDate ? formatDateToLocal(selectedDate) : null}
          lockUseDate={activeTab === 'social-media'}
          currentUserId={currentUserId}
        />
      )}

      {/* Drag-and-Drop Confirmation Modal */}
      {showDragConfirm && pendingDragUpdate && (
        <DragConfirmDialog
          pendingDragUpdate={pendingDragUpdate}
          holidays={holidays}
          onCancel={cancelDragUpdate}
          onConfirm={confirmDragUpdate}
        />
      )}

      {/* Click outside to close dropdowns */}
      {(showClientDropdown || showCategoryDropdown || showSubtypeDropdown || showAssigneeDropdown) && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => {
            setShowClientDropdown(false);
            setShowCategoryDropdown(false);
            setShowSubtypeDropdown(false);
            setShowAssigneeDropdown(false);
          }}
        />
      )}

      {/* SOP Modal */}
      <SOPModal
        isOpen={showSOPModal}
        onClose={() => setShowSOPModal(false)}
        clients={clients}
        departments={departments}
        employees={employees}
        holidays={holidays}
        api={api}
        userType={userType}
        onSuccess={() => {
          fetchTasks({ silent: true });
          setShowSOPModal(false);
        }}
        loading={sopLoading}
        setLoading={setSOPLoading}
      />

      {/* Day Detail Dialog - Shows all tasks for a specific day */}
      <Dialog open={!!dayDetailDate} onOpenChange={(open) => !open && setDayDetailDate(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto p-0 gap-0 bg-white dark:bg-[#1a1a1a]">
          <div className={`${colors.bg} text-white px-6 py-4 flex items-center justify-between`}>
            <div>
              <h2 className="text-lg font-bold">
                {dayDetailDate && dayDetailDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </h2>
              <p className="text-sm text-white/80">
                {dayDetailDate && getTasksForDate(dayDetailDate).length} task{dayDetailDate && getTasksForDate(dayDetailDate).length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="p-4 space-y-2">
            {dayDetailDate && getTasksForDate(dayDetailDate).map((task, i) => {
              const SubtypeIcon = getSubtypeIcon(task.task_subtype);
              const clientColor = getClientColor(task);
              const clientName = getClientName(task);
              return (
                <div
                  key={task._id || i}
                  onClick={() => { setDayDetailDate(null); handleTaskClick({ stopPropagation: () => {} }, task); }}
                  className="flex items-center gap-3 p-3 rounded-xl border border-white/40 dark:border-white/10 hover:bg-neutral-50 dark:hover:bg-white/5 cursor-pointer transition-colors"
                  data-testid={`day-detail-task-${task._id}`}
                >
                  <div 
                    className="w-1 h-10 rounded-full flex-shrink-0"
                    style={{ backgroundColor: clientColor || (colors.bg.includes('pink') ? '#EC4899' : '#3B82F6') }}
                  />
                  <SubtypeIcon className="w-4 h-4 flex-shrink-0 text-neutral-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1a1a2e] truncate">{task.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {clientName && <span className="text-xs text-neutral-500">{clientName}</span>}
                      {task.task_subtype && <span className="text-xs px-1.5 py-0.5 bg-neutral-100 dark:bg-white/10 rounded-full text-neutral-600">{task.task_subtype}</span>}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    task.status === 'completed' ? 'bg-green-100 text-green-700' :
                    task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    task.status === 'review' ? 'bg-purple-100 text-purple-700' :
                    task.status === 'revision' ? 'bg-orange-100 text-orange-700' :
                    'bg-neutral-100 text-neutral-700'
                  }`}>
                    {task.status?.replace('_', ' ') || 'New'}
                  </span>
                </div>
              );
            })}
            {dayDetailDate && getTasksForDate(dayDetailDate).length === 0 && (
              <div className="text-center text-neutral-500 py-8">No tasks for this day</div>
            )}
          </div>
          <div className="border-t border-white/40 dark:border-white/10 p-4">
            <button
              onClick={() => { const date = dayDetailDate; setDayDetailDate(null); setSelectedDate(date); setSelectedTask(null); setShowTaskModal(true); }}
              className={`w-full py-2.5 rounded-xl text-white font-medium ${colors.bg} hover:opacity-90 transition-opacity`}
              data-testid="day-detail-create-task"
            >
              + Create New Task
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
